import type { ExpenseGroup } from './expenseGroups';
import type { CompanyExpense } from '@/hooks/useCompanyExpenses';
import { cn } from '@/lib/utils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface ExpenseTileProps {
  group: ExpenseGroup;
  expenses: CompanyExpense[];
  monthlyTotal: number;
  onClick: () => void;
}

export const ExpenseTile = ({ 
  group, 
  expenses, 
  monthlyTotal, 
  onClick 
}: ExpenseTileProps) => {
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const hasExpenses = expenses.length > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-start p-5 rounded-xl border-2 transition-all duration-200",
        "hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/50",
        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        "text-left w-full min-h-[140px]",
        hasExpenses 
          ? "border-border bg-card" 
          : "border-dashed border-muted-foreground/30 bg-muted/30"
      )}
    >
      {/* Icon & Label */}
      <div className="flex items-center gap-2 mb-3">
        <div className={cn(
          "p-2 rounded-lg",
          hasExpenses ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
        )}>
          {group.icon}
        </div>
        <span className="font-medium text-sm">{group.label}</span>
      </div>

      {/* Total */}
      <div className="flex-1 flex flex-col justify-center">
        {hasExpenses ? (
          <>
            <div className="text-2xl font-bold tracking-tight">
              {formatCurrency(monthlyTotal)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              total this month
            </div>
          </>
        ) : (
          <div className="text-muted-foreground text-sm">
            No expenses
          </div>
        )}
      </div>

      {/* Expense count indicator */}
      <div className="mt-3 flex items-center gap-1.5">
        {hasExpenses ? (
          <>
            <div className="flex gap-0.5">
              {Array.from({ length: Math.min(expenses.length, 5) }).map((_, i) => (
                <div 
                  key={i} 
                  className="w-1.5 h-1.5 rounded-full bg-primary/60" 
                />
              ))}
              {expenses.length > 5 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{expenses.length - 5}
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground ml-1">
              {expenses.length} item{expenses.length !== 1 ? 's' : ''}
            </span>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">
            Click to add
          </span>
        )}
      </div>
    </button>
  );
};
